import { useHasManagementOverviewAccess } from '@features/management-overview/hooks/useHasManagementOverviewAccess';
import type { AppState } from '@protector/entities/__legacy__/redux.types';
import { SetCurrentCompany, SetCurrentProperty } from '@protector/entities/core/__legacy__/redux.actions';
import { Entitlements } from '@protector/entities/licensing/constants';
import { RBACActions, RBACLevels, RBACPermissionTypes } from '@protector/entities/rbac/constants';
import { ManagementOverviewIcon, VisitDashboardIcon } from '@protector/icons';
import '@protector/styles/color-options.variables.css';
import { CropTypesEnum } from '@protector/utils/constants';
import type { Dictionary } from '@protector/utils/types';
import { App } from 'antd';
import { ReactComponent as SvgRiskManagement } from 'assets/images/risk-management.svg';
import OpenInBalance from 'components/open-in-balance/open-in-balance.component';
import { BALANCE_URL } from 'config/constants';
import { COLOR_OPTIONS } from 'config/style';
import { getLicensingStatus, getPermissionFlags, getSelectedCompanyId } from 'core/core.selectors';
import { selectCurrentUser } from 'core/services/auth/auth.reducer';
import STTypo from 'core/shared/typo';
import {
  canAccessTasks,
  userHasNoAccessTasksPrescriptionInRegionalRisk,
  validatePlansAndEntitlements,
  verifyBalanceAppAccess
} from 'core/utils/functions';
import { useHasRiskManagementAccess } from 'core/utils/hooks/use-has-risk-management-access.hook';
import { useCompanyOrOrganizationMatch, useHallOfWorkspaceMatch, useWorkspaceMatch } from 'core/utils/hooks/usePathMatches';
import type { AnalyticsInfo } from 'core/utils/segment/user-events.types';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { CleanLicensingStatus, SetCurrentWorkspaceId } from 'entities/company/company.actions';
import {
  getLoadingLicensingStatus,
  hasHideMenuOptionWithoutSeason,
  hasRTRPlanEntitlement,
  selectHasDiseaseRiskLandingPage
} from 'entities/company/company.selectors';
import { getProductsError } from 'entities/product/product.selectors';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { ClearReports } from 'entities/reports/report.actions';
import { getSelectedSeasonCrops } from 'entities/season/season.reducer';
import { useNewHallOfCompanies } from 'pages/hall-of-entities/hall-of-entities-mfe.hooks';
import { useHasOrganizationPowerBIReports, useHasWorkspacePowerBIReports } from 'pages/powerbi-reports/powerbi-report.hooks';
import { buildPestManagementPath, PestManagementSections, useIsPestManagementSubmenuEnabled } from 'pages/sugarcane/pest-management.routes';
import { useIsRoutesConfigurationEnable } from 'pages/sugarcane/sugarcane.utils';
import { useGetRBACAuthorityUser } from 'querys/rbac/rbac.query';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { To } from 'react-router-dom';
import { useMatch, useSearchParams } from 'react-router-dom';
import SvgNotesGlyph from 'st-protector-icons/Glyph/NotesGlyph';
import SvgCotton from 'st-protector-icons/Minor/Cotton';
import SvgDiseaseRisk from 'st-protector-icons/Minor/DiseaseRisk';
import SvgFieldBook from 'st-protector-icons/Minor/FieldBook';
import SvgFixedPointMenu from 'st-protector-icons/Minor/FixedPointMenu';
import SvgGridOutline from 'st-protector-icons/Minor/GridOutline';
import SvgIntegrations from 'st-protector-icons/Minor/Integrations';
import MyCropwise from 'st-protector-icons/Minor/MyCropwise';
import { SvgNemaDigital } from 'st-protector-icons/Minor/NemaDigital';
import { OpenInNewIcon } from 'st-protector-icons/Minor/OpenInNew';
import SvgOpportunityMachineIcon from 'st-protector-icons/Minor/OpportunityMachineIcon';
import PowerBIReports from 'st-protector-icons/Minor/PowerBIReports';
import SvgSmartSpray from 'st-protector-icons/Minor/SmartSpray';
import SvgSprayOutline from 'st-protector-icons/Minor/SprayOutline';
import SvgStaticPointMenu from 'st-protector-icons/Minor/StaticPointMenu';
import SvgClockTask from 'st-protector-icons/Minor/SvgClockTask';
import SvgTask from 'st-protector-icons/Minor/Task';
import SvgTimeline from 'st-protector-icons/Minor/Timeline';
import SvgWarehouse from 'st-protector-icons/Minor/Warehouse';
import SvgBorerRiskIcon from '../../../st-protector-icons/Minor/BorerRiskIcon';
import SvgControlStrategyIcon from '../../../st-protector-icons/Minor/ControlStrategyIcon';
import SvgRoutingIcon from '../../../st-protector-icons/Minor/RoutingIcon';
import SvgSugarcaneRiskIcon from '../../../st-protector-icons/Minor/SugarcaneRiskIcon';
import MenuIcon from './menu-icon.component';
import { MenuItem } from './menu-item.component';
import type { MenuSubmenuOption } from './menu-submenu.component';
import { MenuSubmenu } from './menu-submenu.component';
import { TrackingFrom } from './menu.enums';
import { asCompanyMatch, handleDiseaseRiskActivation, willShowDiseaseRiskIcon } from './menu.functions';
import './menu.styles.less';

const useCompanyMatchWithOrgFallback = () => {
  const [searchParams] = useSearchParams();
  const matchCompany = useCompanyOrOrganizationMatch();
  const matchFarmSettingsInputsMgt = useMatch({ path: '/farm-settings/inputs-mgt/*', end: false });
  const matchWorkspaceNemaOrg = useMatch({
    path: '/workspace/:workspaceId/nemadigital/map-view/organization/:orgId',
    end: false
  });

  if (matchCompany) return matchCompany;
  if (matchFarmSettingsInputsMgt) return asCompanyMatch(searchParams.get('orgId'));
  if (matchWorkspaceNemaOrg) return asCompanyMatch(matchWorkspaceNemaOrg.params.orgId);
  return null;
};

const STMenu = () => {
  const [t] = useTranslation();
  const matchCompany = useCompanyMatchWithOrgFallback();
  const matchWorkspace = useWorkspaceMatch();

  const isNewHallOfCompanies = useNewHallOfCompanies();
  const userIsInSomeHall = useHallOfWorkspaceMatch(false);
  const matchProperty = useMatch({ path: '/company/:companyId/property/:propertyId', end: false });
  const [diseaseRisk, setDiseaseRisk] = useState<boolean>(false);
  const [borerRisk, setBorerRisk] = useState<boolean>(false);
  const [hasFieldBook, setHasFieldBook] = useState<boolean>(false);
  const [hasSmartPlan, setHasSmartPlan] = useState<boolean>(false);
  const [hasBalanceApp, setHasBalanceApp] = useState<boolean>(false);
  const [opportunityMachine, setOpportunityMachine] = useState<boolean>(false);
  const isLoadingLicensingStatus = useSelector(getLoadingLicensingStatus);
  const licensingStatus = useSelector(getLicensingStatus);
  const companyId = useSelector(getSelectedCompanyId);
  const property = useSelector(getSelectedProperty);
  const productsError = useSelector(getProductsError);
  const permissionFlags = useSelector(getPermissionFlags);
  const systemFlags = useSelector<AppState, Dictionary<boolean | string> | null>(state => state.uiState.global.systemFlags);
  const hasDiseaseRiskLandingPage = useSelector(selectHasDiseaseRiskLandingPage);
  const user = useSelector(selectCurrentUser);
  const [hideTasks, setHideTasks] = useState<boolean>(true);
  const [hideFixedPoints, setHideFixedPoints] = useState<boolean>(false);
  const selectedCrops = useSelector(getSelectedSeasonCrops);
  const dispatch = useDispatch();
  const isPestManagementSubmenuEnabled = useIsPestManagementSubmenuEnabled();
  const [isPostHarvestEnabled, isLeafhopperEnabled] = useIsRoutesConfigurationEnable();
  const isRoutingEnabled = isPostHarvestEnabled || isLeafhopperEnabled;
  const hasPowerBIReports = useMemo(
    () => validatePlansAndEntitlements(licensingStatus, null, [Entitlements.CUSTOMIZED_REPORTS]),
    [licensingStatus]
  );
  const organizationId = companyId;
  const { hasOrganizationReports } = useHasOrganizationPowerBIReports(organizationId);
  const { hasWorkspaceReports } = useHasWorkspacePowerBIReports(matchWorkspace?.params.workspaceId);
  const hasNemaPlanWithoutProtector = useMemo(
    () => validatePlansAndEntitlements(licensingStatus, null, [Entitlements.NEMADIGITAL_WITHOUT_PROTECTOR]),
    [licensingStatus]
  );

  const hasVisitDashboardEntitlement = useMemo(
    () => validatePlansAndEntitlements(licensingStatus, null, [Entitlements.VISIT_MANAGEMENT_DASHBOARD]),
    [licensingStatus]
  );

  const shouldShowNemaMapView = useMemo(
    () =>
      validatePlansAndEntitlements(licensingStatus, null, [Entitlements.SHOW_NEMA_MAP_VIEW_MFE]) ||
      !!systemFlags?.NEMA_1320_show_nema_map_view_mfe,
    [licensingStatus, systemFlags]
  );

  const shouldShowNemaWorkspaceMapView = useMemo(() => !!systemFlags?.NEMA_1276_show_nema_workspace_map_view_mfe, [systemFlags]);

  const shouldRedirectToNemaMapView = useMemo(
    () => hasNemaPlanWithoutProtector && shouldShowNemaMapView,
    [hasNemaPlanWithoutProtector, shouldShowNemaMapView]
  );

  const showOrgTasks = !!systemFlags?.P40_63680_agops_mfe && !matchProperty && canAccessTasks(licensingStatus);

  const showOnlyNotesFeature = useSelector(hasRTRPlanEntitlement);
  const hideMenuOptionWithoutSeason = useSelector(hasHideMenuOptionWithoutSeason);
  const hasRiskManagementAccess = useHasRiskManagementAccess();

  const inventoryPermission = useGetRBACAuthorityUser({
    rbacActions: RBACActions.INVENTORY,
    rbacLevels: RBACLevels.ORG,
    rbacPermission: [RBACPermissionTypes.READ, RBACPermissionTypes.WRITE]
  });

  const sugarcanePermission = useGetRBACAuthorityUser({
    rbacActions: RBACActions.SUGARCANE,
    rbacLevels: RBACLevels.ORG,
    rbacPermission: [RBACPermissionTypes.READ, RBACPermissionTypes.WRITE]
  });

  const timelinePermission = useGetRBACAuthorityUser({
    rbacActions: RBACActions.TIMELINE,
    rbacLevels: RBACLevels.ORG,
    rbacPermission: [RBACPermissionTypes.NOT_READ]
  });

  const {
    simpleCompanyDashboard,
    hidePropertyDashboard,
    hideWarehouse,
    hideSmartSpray,
    hideIntegrations,
    hideTrapsManagement,
    nemaDigital
  } = permissionFlags;

  useEffect(() => {
    const hasOpportunityMachineEntitlement = validatePlansAndEntitlements(licensingStatus, null, [Entitlements.OPPORTUNITY_MACHINE]);

    const shouldEnableOpportunityMachine = !!sugarcanePermission?.length && hasOpportunityMachineEntitlement;

    setOpportunityMachine(shouldEnableOpportunityMachine);
  }, [licensingStatus, sugarcanePermission]);

  const { notification } = App.useApp();

  useEffect(() => {
    if (productsError) {
      notification.error({ title: t('general.spray_error_message') });
    }
  }, [productsError, t, notification]);

  useEffect(() => {
    const fieldbook = validatePlansAndEntitlements(licensingStatus, null, [Entitlements.FIELD_BOOK_SPAIN, Entitlements.FIELD_BOOK_HUNGARY]);
    const smartPlan = validatePlansAndEntitlements(licensingStatus, null, [Entitlements.SMART_PLAN]);
    const balanceApp = verifyBalanceAppAccess(licensingStatus);
    setHasFieldBook(fieldbook);
    setHasSmartPlan(smartPlan);
    setHasBalanceApp(balanceApp && !!systemFlags?.P40_44342_interoperability_balance_protector);
  }, [licensingStatus, systemFlags]);

  useEffect(() => {
    handleDiseaseRiskActivation(property, selectedCrops, companyId, licensingStatus, setDiseaseRisk);
  }, [property, selectedCrops, companyId, licensingStatus]);

  useEffect(() => {
    setBorerRisk(false);
    if (!property || !companyId || !licensingStatus?.apps || !selectedCrops?.length) return;
    const hasBorerRisk = validatePlansAndEntitlements(licensingStatus, null, [Entitlements.BORER_RISK]);
    setBorerRisk(hasBorerRisk && selectedCrops.some(c => c.wk_slug.toLowerCase() === CropTypesEnum.SUGARCANE.toLowerCase()));
    setHideTasks(userHasNoAccessTasksPrescriptionInRegionalRisk(licensingStatus));

    setHideFixedPoints(validatePlansAndEntitlements(licensingStatus, null, [Entitlements.HIDE_FIXED_POINTS]));
  }, [property, companyId, licensingStatus, selectedCrops, systemFlags]);

  const validateNotes = useMemo(() => {
    if (!licensingStatus) return undefined;

    const notesPage = validatePlansAndEntitlements(licensingStatus, null, [Entitlements.NOTES_PAGE]);
    return notesPage && systemFlags?.P40_24023_notes_module;
  }, [systemFlags, licensingStatus]);

  const segmentTracking = useSegmentTracking();

  const trackSegment = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, action: string) => {
      event.stopPropagation();
      const payload: AnalyticsInfo = {
        userId: user?.id ?? '',
        baseRole: user?.role ?? '',
        protectorRole: user?.job_title ?? '',
        from: TrackingFrom.SIDEBAR_MENU
      };

      segmentTracking.track(action, payload);
    },
    [segmentTracking, user?.id, user?.job_title, user?.role]
  );

  const trackDropdownSegment = useCallback(
    (action: string, from: string) => {
      const payload: AnalyticsInfo = {
        userId: user?.id ?? '',
        baseRole: user?.role ?? '',
        protectorRole: user?.job_title ?? '',
        from
      };

      segmentTracking.track(action, payload);
    },
    [segmentTracking, user?.id, user?.job_title, user?.role]
  );

  const handleClickMyCropwise = useCallback(() => {
    dispatch(SetCurrentProperty(null));
    dispatch(SetCurrentWorkspaceId(null));
    dispatch(SetCurrentCompany(null));
    dispatch(CleanLicensingStatus());
    dispatch(ClearReports());
  }, [dispatch]);

  const myCropwiseIcon = () => (
    <MenuItem
      end
      testId='menu-btn-my-cropwise'
      isSelected={userIsInSomeHall}
      to='/hall-of-companies'
      title={t('pages.side_menu.my_cropwise')}
      icon={<MyCropwise fill='#evenodd' />}
      label={t('pages.side_menu.my_cropwise')}
      onClick={handleClickMyCropwise}
    />
  );

  const renderPowerBI = (link: To, segment: string) => {
    return (
      <MenuItem
        end
        testId='menu-btn-powerbi-reports'
        to={link}
        title={t('pages.side_menu.powerbi_reports')}
        icon={<PowerBIReports fill='#707374' />}
        label={t('pages.side_menu.powerbi_reports')}
        onClick={e => trackSegment(e, segment)}
      />
    );
  };

  const hasManagementOverviewAccess = useHasManagementOverviewAccess();

  const managementOverviewButton = hasManagementOverviewAccess && (
    <MenuItem
      target='_blank'
      testId='menu-btn-c-level-report'
      to={`/company/${matchCompany?.params.companyId}/overview`}
      title={t('pages.side_menu.c_level')}
      icon={<ManagementOverviewIcon width={24} height={24} color={COLOR_OPTIONS.NEUTRAL_60} />}
      label={t('pages.side_menu.c_level')}
      onClick={e => trackSegment(e, `C Level Report`)}
    />
  );

  const companyDashboardPage =
    !simpleCompanyDashboard?.value &&
    !validatePlansAndEntitlements(licensingStatus, null, [Entitlements.HIDE_PROPERTY_HALL]) &&
    !isNewHallOfCompanies ? (
      <MenuItem
        end
        testId='menu-btn-company-dashboard'
        to={`/company/${matchCompany?.params.companyId}`}
        title={t('pages.side_menu.hall_of_company')}
        icon={<SvgGridOutline />}
        label={t('pages.side_menu.hall_of_company')}
      />
    ) : null;
  const shouldShowPowerBI = hasOrganizationReports || hasPowerBIReports;
  const regionalRiskPage = hasRiskManagementAccess ? (
    <MenuItem
      testId='menu-btn-regional-risk'
      to={`/company/${matchCompany?.params.companyId}/regional-overview`}
      title={t('pages.regional_overview.title')}
      icon={<SvgRiskManagement height={24} width={24} />}
      label={t('pages.regional_overview.title')}
    />
  ) : null;
  const handleCompany = (
    <>
      {regionalRiskPage}
      {companyDashboardPage}
      {hasSmartPlan && (
        <MenuItem
          end
          testId='menu-btn-company-smartplan'
          to={`/company/${matchCompany?.params.companyId}/smart-plan`}
          title={t('pages.smartplan.title')}
          icon={<SvgClockTask />}
          label={t('pages.smartplan.title')}
        />
      )}

      {!!inventoryPermission?.length && !hideWarehouse.value && (
        <MenuItem
          end
          testId='menu-btn-company-warehouse'
          target={hasBalanceApp ? '_blank' : ''}
          to={
            hasBalanceApp
              ? `${BALANCE_URL}stock/summary?organizationId=${matchCompany?.params.companyId}`
              : `/company/${matchCompany?.params.companyId}/warehouses`
          }
          title={
            <div>
              <STTypo>{t('pages.warehouse.title')}</STTypo>
              {hasBalanceApp && <OpenInBalance />}
            </div>
          }
          icon={<SvgWarehouse />}
          label={t('pages.warehouse.title')}
        />
      )}
      {hasFieldBook && (
        <MenuItem
          end
          testId='menu-btn-company-field-book'
          to={`/company/${matchCompany?.params.companyId}/field-book`}
          title={t('pages.field_book.menu_title')}
          icon={<SvgFieldBook />}
          label={t('pages.field_book.menu_title')}
        />
      )}
      {showOrgTasks && (
        <MenuItem
          end
          testId='menu-btn-company-tasks'
          to={`/company/${matchCompany?.params.companyId}/tasks`}
          title={t('pages.side_menu.tasks')}
          icon={<SvgTask />}
          label={t('pages.side_menu.tasks')}
        />
      )}
      {opportunityMachine && (
        <MenuItem
          end
          testId='menu-btn-opportunities-dashboard'
          to={`/company/${matchCompany?.params.companyId}/opportunity-machine`}
          title={t('pages.opportunity_machine.title')}
          icon={<SvgOpportunityMachineIcon fill='#707374' />}
          label={t('pages.opportunity_machine.title')}
        />
      )}
      {managementOverviewButton}
      {shouldShowPowerBI &&
        renderPowerBI(`/company/${matchCompany?.params.companyId}/reports`, 'User accesses customized report by hall of properties')}
    </>
  );

  const getDiseaseRiskIcon = useCallback(() => {
    return (
      willShowDiseaseRiskIcon(diseaseRisk, property) && (
        <MenuIcon
          data-testid='menu-btn-disease-risk'
          segmentMessage='Open Disease Risk'
          title={t('pages.disease_risk.configuration.name')}
          name={t('pages.disease_risk.configuration.name')}
          icon={<SvgDiseaseRisk fill='#707374' />}
        />
      )
    );
  }, [diseaseRisk, property, t]);

  const notesPage = validateNotes ? (
    <MenuItem
      end
      testId='menu-btn-notes'
      to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/notes`}
      title={t('pages.side_menu.notes')}
      icon={<SvgNotesGlyph height={24} width={24} />}
      label={t('pages.side_menu.notes')}
      onClick={e => trackSegment(e, `Notes - Click on side menu`)}
    />
  ) : null;

  const renderPestManagement = () => {
    const hasSugarcanePestManagement = !!sugarcanePermission?.length && borerRisk && !!property;

    if (!isPestManagementSubmenuEnabled) {
      return (
        hasSugarcanePestManagement && (
          <MenuItem
            testId='menu-btn-borer-risk'
            to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/borer-risk`}
            title={t('pages.borer_risk.title')}
            icon={<SvgBorerRiskIcon fill='#707374' />}
            label={t('pages.borer_risk.title')}
            onClick={e => trackSegment(e, `Open Borer Risk`)}
          />
        )
      );
    }

    const companyId = matchCompany?.params.companyId;
    const propertyId = matchProperty?.params.propertyId;
    const options: MenuSubmenuOption[] = [];

    if (isRoutingEnabled) {
      options.push({
        key: PestManagementSections.ROUTING,
        testId: 'menu-btn-pest-management-routing',
        label: t('post_harvest.scouting_plan_page.header'),
        icon: <SvgRoutingIcon />,
        to: buildPestManagementPath(companyId, propertyId, PestManagementSections.ROUTING),
        onClick: () => segmentTracking.track('Open Routing')
      });
    }

    if (hasSugarcanePestManagement) {
      options.push(
        {
          key: PestManagementSections.CONTROL_STRATEGY,
          testId: 'menu-btn-pest-management-control-strategy',
          label: t('pages.sugarcane.sugarcane_tabs.control_strategy'),
          icon: <SvgControlStrategyIcon />,
          to: buildPestManagementPath(companyId, propertyId, PestManagementSections.CONTROL_STRATEGY),
          onClick: () => segmentTracking.track('Open Control Strategy')
        },
        {
          key: PestManagementSections.RISK,
          testId: 'menu-btn-pest-management-risk',
          label: t('pages.sugarcane.sugarcane_tabs.risk'),
          icon: <SvgSugarcaneRiskIcon />,
          to: buildPestManagementPath(companyId, propertyId, PestManagementSections.RISK),
          onClick: () => segmentTracking.track('Open Borer Risk')
        }
      );
    }

    if (!options.length) {
      return null;
    }

    return (
      <MenuSubmenu
        testId='menu-btn-pest-management'
        label={t('pages.borer_risk.title')}
        icon={<SvgBorerRiskIcon fill='#707374' />}
        options={options}
      />
    );
  };

  const handleProperty = (
    <>
      {!hidePropertyDashboard.value &&
        (hasManagementOverviewAccess ? (
          <MenuSubmenu
            end
            showTitle={false}
            testId='menu-btn-property-dashboard'
            label={t('pages.side_menu.hall_of_properties')}
            icon={<SvgGridOutline />}
            onClick={e => trackSegment(e, `Open Property Dashboard`)}
            options={[
              {
                key: 'property-overview',
                testId: 'menu-btn-property-overview',
                label: t('pages.side_menu.hall_of_properties'),
                icon: <SvgGridOutline />,
                to: `/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}`,
                end: true,
                onClick: () => trackDropdownSegment('Open Property Dashboard', TrackingFrom.SIDEBAR_MENU)
              },
              {
                key: 'management-overview',
                testId: 'menu-btn-management-overview',
                label: t('pages.side_menu.hall_of_company'),
                icon: <ManagementOverviewIcon width={24} height={24} color={COLOR_OPTIONS.NEUTRAL_60} />,
                extra: <OpenInNewIcon fill={COLOR_OPTIONS.NEUTRAL_60} />,
                to: `/company/${matchCompany?.params.companyId}/overview`,
                target: '_blank',
                onClick: () => trackDropdownSegment('C Level Report', TrackingFrom.SIDEBAR_MENU_DROPDOWN)
              }
            ]}
          />
        ) : (
          <MenuItem
            end
            testId='menu-btn-property-dashboard'
            to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}`}
            title={t('pages.side_menu.hall_of_properties')}
            icon={<SvgGridOutline />}
            label={t('pages.side_menu.hall_of_properties')}
            onClick={e => trackSegment(e, `Open Property Dashboard`)}
          />
        ))}
      {hasDiseaseRiskLandingPage && getDiseaseRiskIcon()}
      {notesPage}
      {!timelinePermission?.length && !shouldRedirectToNemaMapView && (
        <MenuItem
          end
          testId='menu-btn-timeline'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/timeline`}
          title={t('pages.side_menu.timeline')}
          icon={<SvgTimeline />}
          label={t('pages.side_menu.timeline')}
          onClick={e => trackSegment(e, `Open Timeline`)}
        />
      )}
      {nemaDigital?.value && shouldShowNemaMapView && (
        <MenuItem
          end
          testId='menu-btn-nemadigital-map-view'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/nemadigital/map-view`}
          title={t('pages.side_menu.nemadigital_map_view')}
          icon={<SvgNemaDigital />}
          label={t('pages.side_menu.nemadigital_map_view')}
          onClick={e => trackSegment(e, `Open NemaDigital Map View from Protector Sidebar`)}
        />
      )}
      {hideTasks && (
        <MenuItem
          end
          testId='menu-btn-tasks'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/tasks`}
          title={t('pages.side_menu.tasks')}
          icon={<SvgTask />}
          label={t('pages.side_menu.tasks')}
          onClick={e => trackSegment(e, `Open Tasks`)}
        />
      )}
      {!hideFixedPoints && (
        <MenuItem
          end
          testId='menu-btn-fixed-points'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/fixed-points`}
          title={t('pages.side_menu.fixed_points')}
          icon={<SvgFixedPointMenu />}
          label={t('pages.side_menu.fixed_points')}
          onClick={e => trackSegment(e, `Open Fixed Points`)}
        />
      )}
      {!hideTrapsManagement.value && (
        <MenuItem
          end
          testId='menu-btn-static-points'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/static-points`}
          title={t('pages.side_menu.traps_management')}
          icon={<SvgStaticPointMenu />}
          label={t('pages.side_menu.traps_management')}
          onClick={e => trackSegment(e, `Open Traps Management`)}
        />
      )}
      {!systemFlags?.P40_63680_agops_mfe && (
        <MenuItem
          end
          testId='menu-btn-spray-registration'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/tasks/spray-registration`}
          title={t('pages.side_menu.spray_registration')}
          icon={<SvgSprayOutline />}
          label={t('pages.side_menu.spray_registration')}
          onClick={e => trackSegment(e, 'Open Spray Registration')}
        />
      )}
      {hasSmartPlan && (
        <MenuItem
          end
          testId='menu-btn-company-smartplan'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/smart-plan`}
          title={t('pages.smartplan.title')}
          icon={<SvgClockTask />}
          label={t('pages.smartplan.title')}
        />
      )}
      {(hasBalanceApp || (!!inventoryPermission?.length && !hideWarehouse.value)) && (
        <MenuItem
          end
          testId='menu-btn-property-warehouse'
          target={hasBalanceApp ? '_blank' : ''}
          to={
            hasBalanceApp
              ? `${BALANCE_URL}stock/summary?organizationId=${matchCompany?.params.companyId}&farmId=${matchProperty?.params.propertyId}`
              : `/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/warehouses`
          }
          title={
            <div>
              <STTypo>{t('pages.warehouse.title')}</STTypo>
              {hasBalanceApp && <OpenInBalance />}
            </div>
          }
          icon={<SvgWarehouse />}
          label={t('pages.warehouse.title')}
          onClick={e => trackSegment(e, `Open Warehouse Property`)}
        />
      )}

      {!hideSmartSpray.value && (
        <MenuItem
          end
          testId='menu-btn-smart-spray'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/smart-spray`}
          title={t('pages.smart-spray.title')}
          icon={<SvgSmartSpray className='smart-spray-icon' />}
          label={t('pages.smart-spray.title')}
          onClick={e => trackSegment(e, `Open Smart Spray`)}
        />
      )}
      {!!selectedCrops?.length && selectedCrops.find(c => c.wk_slug.toLowerCase() === CropTypesEnum.COTTON.toLowerCase()) && (
        <MenuItem
          end
          testId='menu-btn-cotton-growth'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/cotton`}
          title={t('pages.side_menu.cotton')}
          icon={<SvgCotton />}
          label={t('pages.side_menu.cotton')}
          onClick={e => trackSegment(e, `Open Cotton Growth`)}
        />
      )}
      {!hideIntegrations.value && !systemFlags?.P40_45546_HIDE_INTEGRATIONS_OPTION && (
        <MenuItem
          testId='menu-btn-integrations'
          to={`/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/integrations`}
          title={t('pages.integrations.title')}
          icon={<SvgIntegrations className='integrations-icon' />}
          label={t('pages.integrations.title')}
          onClick={e => trackSegment(e, `Open Integrations`)}
        />
      )}
      {!hasDiseaseRiskLandingPage && getDiseaseRiskIcon()}
      {renderPestManagement()}

      {shouldShowPowerBI &&
        renderPowerBI(
          `/company/${matchCompany?.params.companyId}/property/${matchProperty?.params.propertyId}/reports`,
          `User access customized reports by property menu`
        )}
    </>
  );

  // Workspace-scoped entry; forwards the previewed org so the map-view seeds its drill-down there.
  const renderWorkspaceNemaMapView = () => {
    if (!(matchWorkspace && nemaDigital?.value && shouldShowNemaWorkspaceMapView)) {
      return null;
    }
    const orgId = matchCompany?.params.companyId;
    const baseRoute = `/workspace/${matchWorkspace.params.workspaceId}/nemadigital/map-view`;
    return (
      <MenuItem
        testId='menu-btn-workspace-nemadigital-map-view'
        to={orgId ? `${baseRoute}/organization/${orgId}` : `/workspace/${matchWorkspace.params.workspaceId}/nemadigital/my-region`}
        title={t('pages.side_menu.nemadigital_map_view')}
        icon={<SvgNemaDigital />}
        label={t('pages.side_menu.nemadigital_map_view')}
        onClick={event => trackSegment(event, `Open NemaDigital Map View from Workspace Sidebar`)}
      />
    );
  };

  const renderWorkspaceVisitDashboard = () => {
    if (!(matchWorkspace && systemFlags?.P40_62783_visit_dashboard_preview && hasVisitDashboardEntitlement)) {
      return null;
    }
    return (
      <MenuItem
        target='_blank'
        rel='noopener noreferrer'
        testId='menu-btn-workspace-visit-dashboard'
        to={`/workspace/${matchWorkspace.params.workspaceId}/visit-dashboard`}
        title={t('pages.side_menu.visit_dashboard')}
        icon={<VisitDashboardIcon width={24} height={24} />}
        label={t('pages.side_menu.visit_dashboard')}
        onClick={e => trackSegment(e, 'Open Visit Dashboard from Workspace Sidebar')}
      />
    );
  };

  const handleWorkspace = () => {
    const shouldRenderPowerBI = hasWorkspaceReports;
    return (
      <>
        {renderWorkspaceNemaMapView()}
        {shouldRenderPowerBI &&
          renderPowerBI(`/workspace/${matchWorkspace?.params.workspaceId}/reports`, `User access customized reports by workspace hall`)}
        {renderWorkspaceVisitDashboard()}
      </>
    );
  };

  const defineHandleCompany = () => {
    if (showOnlyNotesFeature && hideMenuOptionWithoutSeason) {
      return companyDashboardPage;
    }
    return handleCompany;
  };

  const defineHandleProperty = () => {
    if (showOnlyNotesFeature && hideMenuOptionWithoutSeason) {
      return notesPage;
    }
    return handleProperty;
  };

  const renderContent = () => {
    if (isLoadingLicensingStatus) {
      return null;
    }
    if (matchProperty) {
      return defineHandleProperty();
    } else if (matchCompany) {
      // Keep the workspace map-view entry while previewing an org in the hall (no-op outside the hall).
      return (
        <>
          {renderWorkspaceNemaMapView()}
          {defineHandleCompany()}
        </>
      );
    } else {
      return handleWorkspace();
    }
  };

  return (
    <nav className='st-menu-container'>
      {isNewHallOfCompanies && myCropwiseIcon()}
      {renderContent()}
    </nav>
  );
};

export default STMenu;
